import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SuggestedMatches = ({ suggestions }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })?.format(date);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  const getDistanceText = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance?.toFixed(1)}km`;
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'location': return 'MapPin';
      case 'friends': return 'Users';
      case 'skill': return 'Target';
      case 'time': return 'Clock';
      case 'venue': return 'Building';
      default: return 'Sparkles';
    }
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'location': return 'text-primary';
      case 'friends': return 'text-success';
      case 'skill': return 'text-secondary';
      case 'time': return 'text-warning';
      case 'venue': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getReasonBg = (reason) => {
    switch (reason) {
      case 'location': return 'bg-primary/10';
      case 'friends': return 'bg-success/10';
      case 'skill': return 'bg-secondary/10';
      case 'time': return 'bg-warning/10';
      case 'venue': return 'bg-accent/10';
      default: return 'bg-muted/50';
    }
  };

  const handleJoinMatch = (matchId) => {
    navigate('/match-details', { 
      state: { 
        matchId: matchId,
        action: 'join'
      } 
    });
  };

  const handleViewDetails = (matchId) => {
    navigate('/match-details', { 
      state: { 
        matchId: matchId
      } 
    });
  };

  const nextSuggestion = () => {
    setCurrentIndex((prev) => (prev + 1) % suggestions?.length);
  };

  const prevSuggestion = () => {
    setCurrentIndex((prev) => (prev - 1 + suggestions?.length) % suggestions?.length);
  };

  if (!suggestions || suggestions?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
        <div className="text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
            <Icon name="Search" size={20} className="text-muted-foreground" />
          </div>
          <h3 className="font-heading font-semibold text-card-foreground mb-2">
            Nenhuma sugestão disponível
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Complete seu perfil para receber sugestões personalizadas
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/user-profile')}
            iconName="Settings"
            iconPosition="left"
            iconSize={16}
          >
            Configurar Perfil
          </Button>
        </div>
      </div>
    );
  }

  const currentSuggestion = suggestions?.[currentIndex];

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="Sparkles" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-card-foreground">
                Sugestões para Você
              </h2>
              <p className="text-sm text-muted-foreground">
                {suggestions?.length} pelada{suggestions?.length > 1 ? 's' : ''} recomendada{suggestions?.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Navigation */}
          {suggestions?.length > 1 && (
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={prevSuggestion}
                disabled={suggestions?.length <= 1}
              >
                <Icon name="ChevronLeft" size={16} />
              </Button>
              <span className="text-sm text-muted-foreground font-data">
                {currentIndex + 1}/{suggestions?.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextSuggestion}
                disabled={suggestions?.length <= 1}
              >
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Suggestion Card */}
      <div className="p-4">
        <div className="space-y-4">
          {/* Match Info */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg text-card-foreground mb-1">
                {currentSuggestion?.title}
              </h3>
              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="MapPin" size={16} />
                  <span className="text-sm">{currentSuggestion?.venue}</span>
                  <span className="text-xs bg-muted px-2 py-1 rounded">
                    {getDistanceText(currentSuggestion?.distance)}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Calendar" size={16} />
                  <span className="text-sm font-data">
                    {formatDate(currentSuggestion?.date)} • {currentSuggestion?.time}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="CreditCard" size={16} />
                  <span className="text-sm font-data">
                    {formatCurrency(currentSuggestion?.pricePerPlayer)}
                  </span>
                </div>
              </div>
            </div>

            {/* Match Status */}
            <div className="text-right">
              <div className="flex items-center space-x-1 text-success mb-1">
                <Icon name="Users" size={16} />
                <span className="text-sm font-medium">
                  {currentSuggestion?.confirmedPlayers}/{currentSuggestion?.maxPlayers}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {currentSuggestion?.maxPlayers - currentSuggestion?.confirmedPlayers} vagas
              </div>
            </div>
          </div>

          {/* Recommendation Reasons */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-card-foreground">
              Por que recomendamos:
            </h4>
            <div className="flex flex-wrap gap-2">
              {currentSuggestion?.reasons?.map((reason, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${getReasonBg(reason?.type)}`}
                >
                  <Icon 
                    name={getReasonIcon(reason?.type)} 
                    size={14} 
                    className={getReasonColor(reason?.type)} 
                  />
                  <span className={`text-xs font-medium ${getReasonColor(reason?.type)}`}>
                    {reason?.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Mutual Friends */}
          {currentSuggestion?.mutualFriends && currentSuggestion?.mutualFriends?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-card-foreground">
                Amigos participando:
              </h4>
              <div className="flex items-center space-x-2">
                <div className="flex -space-x-2">
                  {currentSuggestion?.mutualFriends?.slice(0, 4)?.map((friend, index) => (
                    <div key={index} className="w-8 h-8 bg-primary rounded-full border-2 border-card flex items-center justify-center">
                      {friend?.avatar ? (
                        <Image 
                          src={friend?.avatar} 
                          alt={friend?.avatarAlt}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <Icon name="User" size={14} color="white" />
                      )}
                    </div>
                  ))}
                  {currentSuggestion?.mutualFriends?.length > 4 && (
                    <div className="w-8 h-8 bg-muted rounded-full border-2 border-card flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        +{currentSuggestion?.mutualFriends?.length - 4}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {currentSuggestion?.mutualFriends?.length > 1 
                    ? `${currentSuggestion?.mutualFriends?.[0]?.name} e mais ${currentSuggestion?.mutualFriends?.length - 1}`
                    : currentSuggestion?.mutualFriends?.[0]?.name
                  }
                </span>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Vagas preenchidas</span>
              <span className="font-medium text-card-foreground">
                {Math.round((currentSuggestion?.confirmedPlayers / currentSuggestion?.maxPlayers) * 100)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-success h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(currentSuggestion?.confirmedPlayers / currentSuggestion?.maxPlayers) * 100}%` 
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => handleJoinMatch(currentSuggestion?.id)}
              iconName="UserPlus"
              iconPosition="left"
              iconSize={16}
              className="flex-1"
              disabled={currentSuggestion?.confirmedPlayers >= currentSuggestion?.maxPlayers}
            >
              {currentSuggestion?.confirmedPlayers >= currentSuggestion?.maxPlayers ? 'Lotada' : 'Participar'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewDetails(currentSuggestion?.id)}
              iconName="Eye"
              iconPosition="left"
              iconSize={16}
              className="flex-1"
            >
              Ver Detalhes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestedMatches;